import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Route,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RouteStateGuard {
  private inRouteSubscriptions: { [key: string]: Subscription } = {};
  private outRouteSubscriptions: { [key: string]: Subscription } = {};

  constructor(private router: Router) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    const url = state.url;
    const pathFromRoot = route.pathFromRoot
      .map((snapshot) => snapshot.routeConfig?.path)
      .join('/');
    const [defaultRoute, children] = this.getDefaults(route.routeConfig);
    const previousState = this.getPreviousState(url);

    this.createSubscriptions(pathFromRoot);

    if (previousState && previousState.toString() !== url) {
      if (
        children.some((child) =>
          previousState.toString().includes(child.toString()),
        )
      ) {
        return previousState;
      }
    }

    if (url !== pathFromRoot) {
      return true;
    }

    return defaultRoute ?? true;
  }

  private createSubscriptions(pathFromRoot: string) {
    if (!this.hasSubscriptions(pathFromRoot)) {
      // These subscriptions are for when the user navigates within the root route. These will save state and navigate to previous state if needed.
      this.inRouteSubscriptions[pathFromRoot] = this.router.events
        .pipe(
          filter(
            (event) =>
              event instanceof NavigationEnd &&
              event.url.startsWith(pathFromRoot),
          ),
        )
        .subscribe((event: NavigationEnd) => {
          const previousForUrl = this.getPreviousState(event.url);

          // Compare paths only (strip query params) — the guard's job is to
          // remember which child route you were on, not to freeze query params.
          // Comparing full URLs would undo deliberate URL edits (e.g. removing
          // a feature-toggle param like enableBatteryView).
          const currentPath = event.url.split('?')[0];
          const previousPath = previousForUrl?.toString().split('?')[0];
          if (previousForUrl && previousPath !== currentPath) {
            this.router.navigateByUrl(previousForUrl);
            return;
          }

          this.saveState(pathFromRoot, event.url);
        });

      // These subscriptions are for when the user navigates away from the root route for which state should be saved. This just unsubscribes from subscriptions.
      this.outRouteSubscriptions[pathFromRoot] = this.router.events
        .pipe(
          filter(
            (event) =>
              event instanceof NavigationEnd &&
              !event.url.startsWith(pathFromRoot),
          ),
        )
        .subscribe((event: NavigationEnd) => {
          this.inRouteSubscriptions[pathFromRoot].unsubscribe();
          this.outRouteSubscriptions[pathFromRoot].unsubscribe();
        });
    }
  }

  private hasSubscriptions(pathFromRoot: string): boolean {
    return (
      this.inRouteSubscriptions[pathFromRoot] &&
      !this.inRouteSubscriptions[pathFromRoot].closed
    );
  }

  private getDefaults(route: Route): [UrlTree, UrlTree[]] | undefined {
    const children = this.getRouteChildren(route);

    const firstChild = children[0];

    return [firstChild, children] ?? undefined;
  }

  private getPreviousState(url: string): UrlTree | undefined {
    const state = localStorage.getItem(this.getStorageKey(url));

    if (state) {
      return this.router.parseUrl(state);
    }

    return undefined;
  }

  private getRouteChildren(route: Route) {
    return route.children.map((child) =>
      this.router.parseUrl(`${route.path}/${child.path}`),
    );
  }

  private saveState(pathFromRoot: string, url: string) {
    const urlTree = this.router.parseUrl(url.replace(pathFromRoot, ''));

    // Save state for root route
    localStorage.setItem(this.getStorageKey(pathFromRoot), url);

    let path = `${pathFromRoot}`;

    // This saves the state down through children
    urlTree.root.children['primary'].segments.forEach((segment) => {
      path = `${path}/${segment.path}`;
      localStorage.setItem(this.getStorageKey(path), url);
    });
  }

  private getStorageKey(key: string): string {
    return `${key}-route-state`;
  }
}
